import * as Yup from 'yup';

import { DateTime } from 'luxon';

import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import UnpublishedRoundedIcon from '@mui/icons-material/UnpublishedRounded';
import TimelapseRoundedIcon from '@mui/icons-material/TimelapseRounded';

import CustomerInfo from './Components/CustomerInfo';
import Inventory from '../Checkout/Components/Inventory';
import Services from '../Checkout/Components/Services';
import Products from '../Checkout/Components/Products';
import Repairs from '../Checkout/Components/Repairs';
import CustomOrder from '../Checkout/Components/CustomOrder';
import EditCustomer from '../Customers/EditCustomer';
import Adjustments from '../Checkout/Components/Adjustments';
import ActionBar from '../Checkout/Components/ActionBar';

import { displayLabelCustomer } from '../../utils/displayLabels';

import SimpleChip from '../../utils/utilities/SimpleChip';
import MultiStatusChip from '../../utils/utilities/MultiStatusChip';

import LineItemList from './Components/LineItemList';

const fields = [
  { 
    fieldType: "debounceSearchSolo",
    formFieldType: "select",
    select: [],
    name: "customer_id",
    label: "Select Customer",
    url: "customers",
    fieldName: "customer_id",
    displayField: "full_name",
    formUpdates: [{name: "client_name", field: "full_name"}, {name: "final_balance", field: "final_balance"}],
    setDisplayField: "client_name",
    displayLabel: displayLabelCustomer,
    newItemTitle: "Customer",
    FormComponent: <EditCustomer dialog={true}/>,
    size: {xs: 12, lg: 6},
    server: true,
  },
  {
    fieldType: "formField",
    formFieldType: "date",
    name: "date",
    label: "Invoice Date",
    type: "date",
    size: {xs: 12, lg: 3},
  },
  {
    fieldType: "formField",
    formFieldType: "date",
    name: "due_date",
    label: "Due Date",
    type: "date",
    size: {xs: 12, lg: 3},
  },
  { 
    fieldType: "debounceSearchSolo",
    formFieldType: "select",
    select: [],
    name: "sales_rep_id",
    label: "Select Sales Rep",
    url: "users",
    conditionStr: '&is_active[$eq]=true',
    fieldName: "user_id",
    displayField: "display_name",
    formUpdates: [{name: "sales_rep", field: "display_name"}],
    size: {xs: 12},
  },
  {
    fieldType: "divider",
    header: "Invoice Details",
    name: "invoice_details",
    size: {xs: 12, lg: 12},
  },
  { 
    fieldType: "Component",
    Component: <CustomerInfo />,
    name: "customer_info",
    size: {xs: 12, lg: 12},
  },
  { 
    fieldType: "Component",
    Component: <Inventory />,
    name: "inventory",
    size: {xs: 12, lg: 12},
  },
  { 
    fieldType: "Component",
    Component: <Services />,
    name: "services",
    size: {xs: 12, lg: 12},
  },
  { 
    fieldType: "Component",
    Component: <Products />,
    name: "products",
    size: {xs: 12, lg: 12},
  },
  { 
    fieldType: "Component",
    Component: <Repairs />,
    name: "repairs",
    size: {xs: 12, lg: 12},
  },
  { 
    fieldType: "Component",
    Component: <CustomOrder />,
    name: "custom_orders",
    size: {xs: 12, lg: 12},
  },
  // {
  //   fieldType: "divider",
  //   header: "Open Invoices",
  //   name: "open_invoices_divider",
  //   checkDisplayField: (values) => { return !!values?.invoice_id},
  //   size: {xs: 12, lg: 12},
  // },
  { 
    fieldType: "Component",
    Component: <Adjustments />,
    name: "adjustments",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "Component",
    Component: <ActionBar />,
    name: "action_bar",
    size: {xs: 12, lg: 12},
  }
]

const tabs = [
  {id: "all", label: "All", Icon: AllInclusiveRoundedIcon },
  {id: "unpaid", label: "Unpaid", Icon: UnpublishedRoundedIcon },
  {id: "partially_paid", label: "Partially Paid", Icon: TimelapseRoundedIcon },
  {id: "paid", label: "Paid", Icon: CheckCircleOutlineRoundedIcon },
]

const initialValues = {
  appointment_id: "",
  customer_id: "",
  final_balance: 0,
  services: [],
  products: [],
  wigs: [],
  repairs: [],
  adjustments: [],
  custom_orders: [],
  apply_credit: false,
  total_amount: 0,
  date: "",
  due_date: "",
  payment_date: "",
  method: '',
  info: '',
  amount: "",
  notes: '',
  card_num: '',
  exp: '',
  cvv: '',
  use_cc_fee: false,
  cc_rate: 0,
  entry: 'manual',
  processed: false
}

const additionalState = {
  // customer: {},
  client_name: '',
  new_payments: [],
  applied_payments: [],
  final_balance: 0,
  sales_rep: '',
}

const headers = [
  {
    label: 'Invoice Num',
    id: 'invoice_num'
  },
  {
    label: 'Customer',
    id: 'client_name'
  },
  {
    label: "Items",
    id: "line_items",
    render: row => <LineItemList line_items={row.line_items} />
  },
  {
    label: 'Total',
    id: 'total',
    currency: true
  },
  {
    label: "Status",
    id: "paid_status",
    multi: true,
    values: [{value: "paid", status: "success", label: "Paid"}, {value: "partially_paid", status: "warning", label: "Partially Paid"}, {value: "unpaid", status: "error", label: "Unpaid"}]
  },
  {
    label: "Balance",
    currency: true,
    id: "balance"
  },
  {
    label: "Sales Rep",
    id: "sales_rep",
    render: row => row.sales_rep ? <SimpleChip label={row.sales_rep} /> : ''
  },
  {
    label: "Invoice Date",
    id: "date",
    dateShort: true
  },
  {
    label: "Due Date",
    id: "due_date",
    dateShort: true
  }
];

const filters = [
  {
    id: "total",
    type: "total",
    label: "Total",
  },
  {
    id: "balance",
    type: "balance",
    label: "Balance",
  },
  {
    id: "date",
    type: "date",
    label: "Date",
  }
]

const schema = Yup.object().shape({});

const parseData = (data) => {
  const parsed = {
    ...data,
    method: '',
    info: '',
    amount: "",
    notes: '',
    card_num: '',
    exp: '',
    cvv: '',
    entry: 'manual',
    processed: false,
    apply_credit: false,
    payment_date: DateTime.now()
  };
  parsed.date = DateTime.fromISO(data.date);
  parsed.due_date = DateTime.fromISO(data.due_date);
  parsed.customer_id = data.customer_id.toString();
  return parsed
}

export { headers, filters, fields, initialValues, additionalState, schema, parseData, tabs };